<template>
  <div class="ags-categories-list">
    <div class="page-header">
      <h1>Datasets</h1>
    </div>
    
    <div v-if="error" class="error-state">
      {{ error }}
    </div>
    
    <!-- Table Toolbar -->
    <table-toolbar
      :searchable="true"
      :search-query="searchQuery"
      :show-refresh="false"
      @search-change="handleSearchChange"
    />
    
    <themed-table
      ref="categoriesTable"
      :api-endpoint="'/api/admin/cresiteiq2/categories'"
      :bulk-update-endpoint="'/api/admin/cresiteiq2/categories/bulk'"
      bulk-update-payload-key="categories"
      :columns="tableColumns"
      :searchable="true"
      :sortable="true"
      :show-refresh="false"
      :bulk-actions="bulkActions"
      :pagination="true"
      :striped="false"
      :hoverable="true"
      aria-label="AGS Categories Table"
      @bulk-action-complete="handleBulkComplete"
      @bulk-action-error="handleBulkError"
      @action="handleAction"
    />

    <!-- Edit Category Modal -->
    <themed-modal 
      name="edit-category-modal"
      :title="editingCategory ? 'Edit Category' : 'Create Category'"
      size="lg"
      @closed="closeEditModal"
    >
      <themed-form
        :key="editingCategory ? editingCategory.id : 'new'"
        :fields="categoryFormFields"
        :model-value="editForm"
        :errors="editFormErrors"
        layout="grid"
        :columns="5"
        @input="editForm = $event"
        @submit="saveCategory"
      >
        <template #after-fields>
          <div v-if="editFormError" class="error-message">
            {{ editFormError }}
          </div>
        </template>
      </themed-form>

      <template #footer>
        <div class="modal-actions">
          <themed-button variant="outline" @click="closeEditModal">
            Cancel
          </themed-button>
          <themed-button 
            variant="default" 
            @click="saveCategory"
            :disabled="savingCategory"
          >
            {{ savingCategory ? 'Saving...' : 'Save Changes' }}
          </themed-button>
        </div>
      </template>
    </themed-modal>

    <!-- Manage Variables Modal -->
    <themed-modal 
      name="variables-modal"
      :title="`Manage Variables - ${selectedCategory ? selectedCategory.display_name : ''}`"
      :width="1100"
      @closed="closeVariablesModal"
    >
      <div class="variables-manager">
          <!-- Variables Table Toolbar -->
          <table-toolbar
            :searchable="true"
            :search-query="variablesSearchQuery"
            :show-refresh="false"
            @search-change="handleVariablesSearchChange"
          />
          
          <themed-table
            ref="variablesTable"
            :api-endpoint="variablesApiEndpoint"
            :bulk-update-endpoint="variablesBulkUpdateEndpoint"
            bulk-update-payload-key="variables"
            :columns="variableColumns"
            :searchable="true"
            :sortable="true"
            :inline-editable="false"
            :bulk-actions="variableBulkActions"
            :pagination="true"
            :striped="true"
            :hoverable="true"
            row-id-field="ags_variable"
            aria-label="Category Variables Table"
            @bulk-action-complete="handleVariableBulkComplete"
            @bulk-action-error="handleVariableBulkError"
            @action="handleVariableAction"
          />
        </div>
    </themed-modal>

    <!-- Edit Variable Modal -->
    <themed-modal 
      name="edit-variable-modal"
      title="Edit Variable"
      size="md"
      @closed="closeEditVariableModal"
    >
      <themed-form
        :key="editingVariable ? editingVariable.ags_variable : 'new'"
        :fields="variableFormFields"
        :model-value="editVariableForm"
        :errors="editVariableFormErrors"
        layout="grid"
        :columns="4"
        @input="editVariableForm = $event"
        @submit="saveVariable"
      >
        <template #after-fields>
          <div v-if="editVariableFormError" class="error-message">
            {{ editVariableFormError }}
          </div>
        </template>
      </themed-form>

      <template #footer>
        <div class="modal-actions">
          <themed-button variant="outline" @click="closeEditVariableModal">
            Cancel
          </themed-button>
          <themed-button 
            variant="default" 
            @click="saveVariable"
            :disabled="savingVariable"
          >
            {{ savingVariable ? 'Saving...' : 'Save Changes' }}
          </themed-button>
        </div>
      </template>
    </themed-modal>
  </div>
</template>

<script>
import TableToolbar from '../../table/TableToolbar.vue';

export default {
  name: 'AgsCategoriesList',
  components: {
    TableToolbar
  },
  data() {
    return {
      loading: false,
      error: null,
      searchQuery: '',
      variablesSearchQuery: '',
      editingCategory: null,
      editForm: {
        category_name: '',
        source: 'AGS',
        display_name: '',
        description: '',
        population_threshold: 2500,
        threshold_type: 'density',
        density_threshold: 350.0,
        is_active: true
      },
      savingCategory: false,
      editFormError: null,
      editFormErrors: {},
      selectedCategory: null,
      editingVariable: null,
      editVariableForm: {
        ags_variable: '',
        description: '',
        type: 'integer',
        unit: '',
        active: true
      },
      savingVariable: false,
      editVariableFormError: null,
      editVariableFormErrors: {},
      variableColumns: [
        {
          field: 'ags_variable',
          label: 'Variable Code',
          type: 'text',
          sortable: true,
          searchable: true,
          width: '150px'
        },
        {
          field: 'description',
          label: 'Description',
          type: 'text',
          sortable: true,
          searchable: true
        },
        {
          field: 'type',
          label: 'Type',
          type: 'text',
          sortable: true,
          searchable: true,
          width: '120px'
        },
        {
          field: 'unit',
          label: 'Unit',
          type: 'text',
          sortable: true,
          searchable: true,
          width: '100px'
        },
        {
          field: 'active',
          label: 'Active',
          type: 'checkbox',
          sortable: true,
          searchable: false,
          width: '100px'
        },
        {
          type: 'actions',
          label: 'Actions',
          width: '80px',
          buttons: [
            {
              name: 'edit',
              icon: 'edit',
              variant: 'ghost',
              size: 'sm'
            }
          ]
        }
      ],
      variableBulkActions: [
        {
          name: 'activate',
          label: 'Activate',
          variant: 'default',
          field: 'active',
          value: true
        },
        {
          name: 'deactivate',
          label: 'Deactivate',
          variant: 'secondary',
          field: 'active',
          value: false
        }
      ],
      tableColumns: [
        {
          field: 'id',
          label: 'ID',
          type: 'number',
          sortable: true,
          searchable: true,
          width: '80px'
        },
        {
          field: 'category_name',
          label: 'Category Name',
          type: 'text',
          sortable: true,
          searchable: true
        },
        {
          field: 'source',
          label: 'Source',
          type: 'text',
          sortable: true,
          searchable: true,
          width: '120px'
        },
        {
          field: 'display_name',
          label: 'Display Name',
          type: 'text',
          sortable: true,
          searchable: true
        },
        {
          field: 'description',
          label: 'Description',
          type: 'text',
          sortable: false,
          searchable: true
        },
        {
          field: 'is_active',
          label: 'Active',
          type: 'checkbox',
          sortable: false,
          searchable: false
        },
        {
          type: 'actions',
          label: 'Actions',
          width: '200px',
          buttons: [
            {
              name: 'edit',
              icon: 'edit',
              variant: 'ghost',
              size: 'sm'
            },
            {
              name: 'variables',
              label: (row) => `Variables (${row.variable_count || 0})`,
              icon: 'variable-icon',
              variant: 'default',
              size: 'sm'
            }
          ]
        }
      ],
      bulkActions: [
        {
          name: 'activate',
          label: 'Activate',
          variant: 'default',
          field: 'is_active',
          value: true
        },
        {
          name: 'deactivate',
          label: 'Deactivate',
          variant: 'secondary',
          field: 'is_active',
          value: false
        }
      ]
    };
  },
  computed: {
    variablesApiEndpoint() {
      if (!this.selectedCategory) {
        return null;
      }
      return `/api/admin/cresiteiq2/categories/${this.selectedCategory.id}/variables`;
    },
    variablesBulkUpdateEndpoint() {
      if (!this.selectedCategory) {
        return null;
      }
      return `/api/admin/cresiteiq2/categories/${this.selectedCategory.id}/variables/bulk`;
    },
    categoryFormFields() {
      const isDensityType = this.editForm.threshold_type === 'density';
      
      return [
        // Row 1: Category Name, Display Name, Source
        {
          name: 'category_name',
          type: 'input',
          label: 'Category Name',
          placeholder: 'e.g., age_income',
          required: !this.editingCategory,
          tooltip: this.editingCategory ? 'This cannot be changed after creation' : null,
          tooltipAlign: 'left',
          componentProps: {
            disabled: !!this.editingCategory,
            readonly: !!this.editingCategory
          },
          col: 2
        },
        {
          name: 'display_name',
          type: 'input',
          label: 'Display Name',
          placeholder: 'e.g., Age & Income',
          required: true,
          col: 2
        },
        {
          name: 'source',
          type: 'input',
          label: 'Source',
          placeholder: 'e.g., AGS',
          required: true,
          tooltip: 'Data source identifier (default: AGS)',
          tooltipAlign: 'center',
          col: 1
        },
        // Row 2: Threshold Type (disabled), Conditional Threshold Field, Active
        {
          name: 'threshold_type',
          type: 'select',
          label: 'Threshold Type',
          required: true,
          tooltip: 'Filtering type is set to density-based (people/sq mi)',
          tooltipAlign: 'left',
          options: [
            { value: 'density', label: 'Density (people/sq mi)' },
            { value: 'population', label: 'Population Count' }
          ],
          componentProps: {
            disabled: true,
            readonly: true
          },
          col: 1
        },
        // Conditional field: Show density_threshold if type is 'density'
        ...(isDensityType ? [{
          name: 'density_threshold',
          type: 'input',
          inputType: 'number',
          label: 'Density Threshold',
          placeholder: '350',
          tooltip: 'Minimum density in people/sq mi required to display data',
          tooltipAlign: 'center',
          componentProps: {
            min: 0,
            max: 100000,
            step: 10
          },
          col: 1
        }] : []),
        // Conditional field: Show population_threshold if type is 'population'
        ...(!isDensityType ? [{
          name: 'population_threshold',
          type: 'input',
          inputType: 'number',
          label: 'Population Threshold',
          placeholder: '2500',
          tooltip: 'Minimum population count required to display data',
          tooltipAlign: 'center',
          componentProps: {
            min: 0,
            max: 1000000,
            step: 100
          },
          col: 1
        }] : []),
        {
          name: 'is_active',
          type: 'toggle',
          label: 'Active',
          col: 1
        },
        // Row 3: Description (full width)
        {
          name: 'description',
          type: 'textarea',
          label: 'Description',
          placeholder: 'Brief description of this category...',
          rows: 3,
          col: 5
        }
      ];
    },
    variableFormFields() {
      return [
        // Row 1: Variable Code, Type, Unit, Active
        {
          name: 'ags_variable',
          type: 'input',
          label: 'Variable Code',
          placeholder: 'e.g., B01001_001E',
          required: true,
          tooltip: 'This cannot be changed',
          tooltipAlign: 'left',
          componentProps: {
            disabled: true,
            readonly: true
          },
          col: 1
        },
        {
          name: 'type',
          type: 'select',
          label: 'Type',
          required: true,
          options: [
            { value: 'integer', label: 'Integer' },
            { value: 'float', label: 'Float' },
            { value: 'string', label: 'String' }
          ],
          col: 1
        },
        {
          name: 'unit',
          type: 'input',
          label: 'Unit',
          placeholder: 'e.g., people, dollars, %',
          col: 1
        },
        {
          name: 'active',
          type: 'toggle',
          label: 'Active',
          col: 1
        },
        // Row 2: Description (full width)
        {
          name: 'description',
          type: 'textarea',
          label: 'Description',
          rows: 3,
          col: 4
        }
      ];
    }
  },
  mounted() {
    // API mode handles data fetching automatically
  },
  methods: {

    handleSearchChange(query) {
      this.searchQuery = query;
      // Trigger table refresh with search query
      if (this.$refs.categoriesTable) {
        this.$refs.categoriesTable.currentSearchQuery = query;
        this.$refs.categoriesTable.refresh();
      }
    },

    handleVariablesSearchChange(query) {
      this.variablesSearchQuery = query;
      // Trigger variables table refresh with search query
      if (this.$refs.variablesTable) {
        this.$refs.variablesTable.currentSearchQuery = query;
        this.$refs.variablesTable.refresh();
      }
    },

    handleBulkComplete({ action, selected, results }) {
      const count = selected.length;
      const message = results?.data?.message || `Successfully updated ${count} ${count === 1 ? 'category' : 'categories'}`;
      this.$toast.success(message);
    },

    handleBulkError({ action, selected, errors }) {
      const message = errors?.response?.data?.message || 'Failed to perform bulk action';
      this.$toast.error(message);
    },

    handleAction({ action, row }) {
      if (action === 'edit') {
        this.openEditModal(row);
      } else if (action === 'variables') {
        this.openVariablesModal(row);
      }
    },

    openEditModal(category) {
      this.editingCategory = category;
      this.editForm = {
        category_name: category.category_name,
        source: category.source || 'AGS',
        display_name: category.display_name,
        description: category.description || '',
        population_threshold: category.population_threshold !== null ? category.population_threshold : 2500,
        threshold_type: category.threshold_type || 'density',
        density_threshold: category.density_threshold !== null ? category.density_threshold : 350.0,
        is_active: category.is_active
      };
      this.editFormError = null;
      this.$modal.show('edit-category-modal');
    },
    closeEditModal() {
      this.$modal.hide('edit-category-modal');
      this.editingCategory = null;
      this.editForm = {
        category_name: '',
        source: 'AGS',
        display_name: '',
        description: '',
        population_threshold: 2500,
        threshold_type: 'density',
        density_threshold: 350.0,
        is_active: true
      };
      this.editFormError = null;
      this.editFormErrors = {};
      this.savingCategory = false;
    },
    async saveCategory() {
      this.savingCategory = true;
      this.editFormError = null;
      this.editFormErrors = {};

      try {
        if (this.editingCategory) {
          await window.axios.put(
            `/api/admin/cresiteiq2/categories/${this.editingCategory.id}`,
            this.editForm
          );
          this.$toast.success('Category updated successfully');
        } else {
          await window.axios.post(
            '/api/admin/cresiteiq2/categories',
            this.editForm
          );
          this.$toast.success('Category created successfully');
        }

        this.closeEditModal();
        this.$refs.categoriesTable?.refresh();
      } catch (error) {
        // Set field-level validation errors for ThemedForm
        if (error.response?.data?.errors) {
          this.editFormErrors = error.response.data.errors;
        }
        
        // Set general form-level error message
        this.editFormError = error.response?.data?.message || 'Failed to save category';
        this.$toast.error(this.editFormError);
      } finally {
        this.savingCategory = false;
      }
    },

    openVariablesModal(category) {
      this.selectedCategory = category;
      this.$modal.show('variables-modal');
    },
    closeVariablesModal() {
      this.$modal.hide('variables-modal');
      this.selectedCategory = null;
    },
    
    handleVariableAction({ action, row }) {
      if (action === 'edit') {
        this.openEditVariableModal(row);
      }
    },
    
    openEditVariableModal(variable) {
      this.editingVariable = variable;
      this.editVariableForm = {
        ags_variable: variable.ags_variable,
        description: variable.description || '',
        type: variable.type || 'integer',
        unit: variable.unit || '',
        active: variable.active !== undefined ? variable.active : true
      };
      this.editVariableFormError = null;
      this.$modal.show('edit-variable-modal');
    },
    
    closeEditVariableModal() {
      this.$modal.hide('edit-variable-modal');
      this.editingVariable = null;
      this.editVariableForm = {
        ags_variable: '',
        description: '',
        type: 'integer',
        unit: '',
        active: true
      };
      this.editVariableFormError = null;
      this.editVariableFormErrors = {};
      this.savingVariable = false;
    },
    
    async saveVariable() {
      this.savingVariable = true;
      this.editVariableFormError = null;
      this.editVariableFormErrors = {};

      try {
        await window.axios.put(
          `/api/admin/cresiteiq2/categories/${this.selectedCategory.id}/variables`,
          this.editVariableForm
        );
        
        this.$toast.success('Variable updated successfully');
        this.closeEditVariableModal();
        this.$refs.variablesTable?.refresh();
      } catch (error) {
        // Set field-level validation errors for ThemedForm
        if (error.response?.data?.errors) {
          this.editVariableFormErrors = error.response.data.errors;
        }
        
        // Set general form-level error message
        this.editVariableFormError = error.response?.data?.message || 'Failed to save variable';
        this.$toast.error(this.editVariableFormError);
      } finally {
        this.savingVariable = false;
      }
    },
    
    handleVariableBulkComplete({ action, selected, results }) {
      const count = selected.length;
      const message = results?.data?.message || `Successfully updated ${count} ${count === 1 ? 'variable' : 'variables'}`;
      this.$toast.success(message);
    },
    
    handleVariableBulkError({ action, selected, errors }) {
      const message = errors?.response?.data?.message || 'Failed to perform bulk action on variables';
      this.$toast.error(message);
    }
  }
};
</script>


<style scoped>
.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  margin: 0;
  font-size: 1.875rem;
  font-weight: 600;
  color: var(--foreground);
}

.error-state {
  padding: 1rem;
  margin-bottom: 1rem;
  background-color: var(--destructive);
  color: var(--destructive-foreground);
  border-radius: 0.375rem;
}

.error-message {
  padding: 0.75rem;
  background-color: var(--destructive);
  color: var(--destructive-foreground);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  margin-top: 1rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.variables-manager {
  min-height: 400px;
}
</style>
